import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { StateCleanupService } from './state-cleanup.service';

export interface ApiKey {
  id: string; // UUID
  name: string;
  api_key_hash: string;
  clear_api_key: string;
  status: string;
  usage_count: number;
  request_quota_limit: number;
  remaining_request_quota: number;
  token_quota_limit: number;
  remaining_token_quota: number;
  created_at: string; // date-time
  updated_at: string; // date-time
}

interface ApiUsage {
  request_usage_count: number;
  request_quota_limit: number;
  token_usage_count: number;
  token_quota_limit: number;
  search_usage_count: number;
  search_quota_limit: number;
  enforce_quota: boolean;
}

interface DocumentUsage {
  current_count: number;
  quota_limit: number;
  current_storage_bytes: number;
  storage_limit_bytes: number;
}

interface FileQuota {
  current_count: number;
  quota_limit: number;
}

interface BotUsage {
  current_count: number;
  quota_limit: number;
}

interface UrlUsage {
  current_count: number;
  quota_limit: number;
}

interface MoodleCourseUsage {
  current_count: number;
  quota_limit: number;
}

interface UsageData {
  api_usage: ApiUsage;
  document_usage: DocumentUsage;
  file_usage: FileQuota;
  bot_usage: BotUsage;
  url_usage: UrlUsage;
  moodle_course_usage: MoodleCourseUsage;
}

@Injectable({
  providedIn: 'root',
})
export class UsageService {
  apiKeys$ = new BehaviorSubject<ApiKey[]>([]);
  usage$ = new BehaviorSubject<UsageData>({} as UsageData);
  loading$ = new BehaviorSubject(false);

  constructor(
    private http: HttpClient,
    private stateCleanupService: StateCleanupService
  ) {
    this.listApiKeys().subscribe();
    this.stateCleanupService.register(() => this.resetState());
  }

  deleteApiKey(id: string) {
    this.loading$.next(true);
    return this.http.delete(`${environment.userBackend}/api-keys/${id}`).pipe(
      tap(() => {
        this.loading$.next(false);
        this.listApiKeys().subscribe();
      })
    );
  }

  createApiKey(apiKey: ApiKey) {
    this.loading$.next(true);
    return this.http.post(`${environment.userBackend}/api-keys`, apiKey).pipe(
      tap(() => {
        this.loading$.next(false);
        this.listApiKeys().subscribe();
      })
    );
  }

  listApiKeys() {
    this.loading$.next(true);
    return this.http.get(`${environment.userBackend}/api-keys`).pipe(
      map((data: any) => data.data),
      tap((data: ApiKey[]) => {
        this.apiKeys$.next(data);
        this.loading$.next(false);
      })
    );
  }

  private resetState() {
    this.apiKeys$.next([]);
    this.usage$.next({} as UsageData);
    this.loading$.next(false);
  }
}
